import type { UploadFile } from 'antd/es/upload'
import axios from 'axios'

import type { WebhookData } from 'src/api/main'
import i18n from 'src/locales/i18n'

import { MAX_SVG_MB, storableFile } from './svgUpload'

type Props = {
  submitWebhook: (webhookData: WebhookData) => Promise<unknown>
  onError?: (error: Error) => void
}

/**
 * PUTs each file to its presigned URL and reports it to the webhook. An SVG is
 * stored as its sanitized copy (`storableFile`), so it must parse as an SVG and
 * stay within `MAX_SVG_MB`; one that does not fails with an API-style
 * `{ message }`, which `showApiErrors` shows as it is.
 */
export const uploadFiles = async (
  files: UploadFile[],
  options: Props
): Promise<void> => {
  const { submitWebhook, onError } = options

  for (const file of files) {
    const uploadUrl = file.response?.upload_url
    const webhookData = file.response?.webhook_data

    if (uploadUrl && webhookData && file.originFileObj) {
      try {
        const stored = await storableFile(file.originFileObj)
        if (typeof stored === 'string') {
          throw {
            message: i18n.t(`svgUpload.${stored}`, { size: MAX_SVG_MB })
          }
        }
        const arrayBuffer = await stored.arrayBuffer()

        await axios.put(uploadUrl, arrayBuffer, {
          withCredentials: false,
          timeout: 30000,
          headers: {
            'Content-Type':
              webhookData?.Records[0]?.s3.object.content_type ||
              'application/octet-stream'
          }
        })

        await submitWebhook(webhookData)
      } catch (error) {
        onError?.(error as Error)
        throw error
      }
    }
  }
}
